import { For, Show, createEffect, createSignal, useContext, createMemo } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { ErrorCatcher } from '../../tools/error-catcher';
import { Grid } from '../../grid-system/grid/grid';
import { Section } from '../../grid-system/section/section';
import theme from '../../style/theme';
import { FilterList } from '../../ui-components/filter-list/filter-list';
import { Heading } from '../../ui-components/heading/heading';
import { Checkbox } from '../../ui-components/inputs/checkbox/checkbox';
import { Radio } from '../../ui-components/inputs/radio/radio';
import { gridSettings, StyledSidebarContainer, StyledSidebarContainerLine, StyledSidebar, StyledSidebarInner } from '../../ui-components/layouts/sidebar.style';
import { StyledFilterHeadingContainer, StyledProductsContainer } from '../products/products.style';
import { LoadingPlaceHolder } from '../loading-place-holder/loading-place-holder';
import { DocumentHolder, Type, Category } from './document-holder';
import { StyledClearAllContainer, StyledDocument, StyledDocumentRow, StyledDocumentsList, StyledHeadingWrapper, StyledSearchInput } from './files.style';
import { Button } from '../../ui-components/button/button';
import { MobileFilesFilter } from './mobile-files-filter';
import { StyledVerticalSpace } from '../../ui-components/utility-style-components/spacing';
import { StyledFlexRow } from '../../ui-components/utility-style-components/flex';
import { gql } from 'graphql-request';
import { getFileSlugTranslations } from './file-translations-map';
import { useSearchParams } from '@solidjs/router';


const GET_FILES_QUERY = gql`
    query GetFiles($typeIds: [ID!]) {
        documentHolders: getDocumentHoldersByTypes(typeIds: $typeIds) {
            id
            uuid
            name
            slug
            identifierId
            categories {
                id
                uuid
                name
                parentId
            }
            files {
                name
                path
            }
            type {
                id
                name
                uuid
            }
        }
    }
`;


const getTypesFromDocumentHolders = (documentHolders: DocumentHolder[]) => {
    const types = documentHolders.map((documentHolder) => {
        return documentHolder.type;
    });
    // Remove duplicates before returning.
    return types.filter((value, index, self) =>
        index === self.findIndex((t) => (
            t.id === value.id && t.name === value.name
        ))
    );
};

export const getSearchParamAsArray = (param: string): string[] => {
    if (!param) {
        return [];
    }
    const paramList = new Set(param.split(',').map(p => p.trim()).filter(p => p.length > 0));
    return Array.from(paramList);
};


export const Files = (props: FilesProps) => {
    const { viewport, localize, createCachedResource } = useContext(AppContext);
    const [isTabletOrSmaller, setIsTabletOrSmaller] = createSignal<boolean>(false);
    const [isMobile, setIsMobile] = createSignal<boolean>(false);
    const [searchQuery, setSearchQuery] = createSignal('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [topCategoriesToShow, setParentCategoriesToShow] = createSignal<Category[]>([]);
    const [subCategoriesToShow, setSubCategoriesToShow] = createSignal<Category[]>([]);
    const [typeIds, setTypeIds] = createSignal(props.types ? [...props.types] : []);
    const [selectedTypeId, setSelectedTypeId] = createSignal<number>();

    const [ documentHoldersData ] = createCachedResource(
        GET_FILES_QUERY,
        () => ({
            typeIds: typeIds(),
        }),
        true
    );

    createEffect(() => {
        const newTypeIds = props.types ? [...props.types] : [];
        setTypeIds(newTypeIds);
    });

    const documentHolders = createMemo<DocumentHolder[]>(() => documentHoldersData()?.documentHolders || []);
    const types = createMemo<Type[]>(() => getTypesFromDocumentHolders(documentHolders()));

    createEffect(() => setIsTabletOrSmaller(viewport.width <= theme.breakpoints.TABLET));
    createEffect(() => setIsMobile(viewport.width <= theme.breakpoints.MOBILE));

    const clearFilters = (e: any) => {
        e.preventDefault();
        setSearchParams({ type: null, categories: null, subcategories: null }, { replace: true });
    };

    // Type changes
    createEffect(() => {
        const typeInUrlParam = searchParams.type;
        const typesExist = types().length > 0;

        if (!typeInUrlParam && typesExist) {
            handleTypeSelect(types()[0]);
        } else if (typeInUrlParam && typesExist) {
            const matchingType = types().find(t => t.name === typeInUrlParam);
            if (matchingType) {
                const categories = documentHolders().flatMap(doc => doc.categories?.filter((cat) => {
                    if (doc.type?.id !== matchingType.id) {
                        return false;
                    }
                    return !cat.parentId;
                }));

                const uniqueParentCategoriesToShow = [...new Map(categories.map(item => [item['id'], item])).values()];

                setParentCategoriesToShow(uniqueParentCategoriesToShow);
                setSelectedTypeId(matchingType.id);
            }
        }
    });

    // Category and SubCategory changes
    createEffect(() => {
        const topCategoriesInUrlParam = searchParams.categories;
        const subCategoriesInUrlParam = searchParams.subcategories;
        const visibleTopCategoriesExist = topCategoriesToShow().length > 0;

        if (topCategoriesInUrlParam && visibleTopCategoriesExist) {
            const validTopCategoriesInUrlParam = topCategoriesToShow().filter(visibleTopCategory => {
                const searchParamCategories = getSearchParamAsArray(topCategoriesInUrlParam);
                return searchParamCategories.includes(visibleTopCategory.name);
            });

            if (validTopCategoriesInUrlParam.length > 0) {
                const subCategories = documentHolders().flatMap(doc => doc.categories?.filter((categoryObject) => {
                    const hasNoParent = !categoryObject.parentId;
                    if (hasNoParent) {
                        return false;
                    }
                    return validTopCategoriesInUrlParam.some(validTopCategory => validTopCategory.id === categoryObject.parentId);
                }));

                if (subCategories.length === 0) {
                    setSubCategoriesToShow([]);
                    setSearchParams({ ...searchParams, subcategories: null }, { replace: true });
                } else {
                    const uniqueSubCategories = [...new Map(subCategories.map(item => [item['name'], item])).values()];
                    setSubCategoriesToShow(uniqueSubCategories);

                    const currentSubCategoriesInUrlParam = getSearchParamAsArray(subCategoriesInUrlParam || '');
                    const validSubCategoriesInUrlParam = uniqueSubCategories.filter(subCategory => currentSubCategoriesInUrlParam.includes(subCategory.name));
                    if (validSubCategoriesInUrlParam.length === 0) {
                        setSearchParams({ ...searchParams, subcategories: null }, { replace: true });
                    } else if (validSubCategoriesInUrlParam.length < currentSubCategoriesInUrlParam.length) {
                        const newSubCategoriesParam = validSubCategoriesInUrlParam.map(cat => cat.name);
                        setSearchParams({ ...searchParams, subcategories: newSubCategoriesParam.join(',') }, { replace: true });
                    }
                }
            } else {
                setParentCategoriesToShow([]);
                setSubCategoriesToShow([]);
                setSearchParams({ ...searchParams, categories: null, subcategories: null }, { replace: true });
            }
        } else if (subCategoriesInUrlParam) {
            setSubCategoriesToShow([]);
            setSearchParams({ ...searchParams, categories: null, subcategories: null }, { replace: true });
        } else {
            setSubCategoriesToShow([]);
        }
    });

    const handleTypeSelect = (type: Type) => {
        setSearchParams({ type: type.name, categories: null, subcategories: null }, { replace: true });
    };

    const addTopCategory = (category: Category) => {
        const currentCategories = getSearchParamAsArray(searchParams.categories);
        if (currentCategories.includes(category.name)) {
            return;
        }
        currentCategories.push(category.name);
        setSearchParams({ ...searchParams, categories: currentCategories.length ? currentCategories.join(',') : null }, { replace: true });
    };

    const removeMainCategory = (category: Category) => {
        const currentCategories = getSearchParamAsArray(searchParams.categories);
        const updatedCategories = currentCategories.filter(catName => catName !== category.name);
        setSearchParams({ ...searchParams, categories: updatedCategories.length ? updatedCategories.join(',') : null }, { replace: true });
    };

    const addSubCategory = (category: Category) => {
        const currentSubCategories = getSearchParamAsArray(searchParams.subcategories);

        if (currentSubCategories.includes(category.name)) {
            return;
        }
        currentSubCategories.push(category.name);
        setSearchParams({ ...searchParams, subcategories: currentSubCategories.length ? currentSubCategories.join(',') : null }, { replace: true });
    };

    const removeSubCategory = (category: Category) => {
        const currentSubCategories = getSearchParamAsArray(searchParams.subcategories);
        const updatedSubCategories = currentSubCategories.filter(catName => catName !== category.name);
        setSearchParams({ ...searchParams, subcategories: updatedSubCategories.length ? updatedSubCategories.join(',') : null }, { replace: true });
    };

    const filteredDocuments = () => {
        const documents = documentHolders().filter((documentHolder) => {
            // First, if there is a search query active, make sure it's matching.
            if (searchQuery().length) {
                const nameMatch = documentHolder.name.toLocaleLowerCase().includes(searchQuery());
                const categoryMatch = documentHolder.categories.some( (cat) => cat.name.toLocaleLowerCase().includes(searchQuery()) );
                if (!nameMatch && !categoryMatch) {
                    return false;
                }
            }

            // If they dont have a filter menu we show every document.
            if (!props.showFilterMenu) {
                return true;
            }

            // Then, if no type has been selected, show it.
            if (!searchParams.type) {
                return true;
            }

            // Then, make sure it has the same type.
            if (documentHolder.type?.name !== searchParams.type) {
                return false;
            }

            // Then, if no top category has been selected, show it.
            if (!searchParams.categories) {
                return true;
            }

            // If categories has been selected, make sure it has at least one matching category with the selected ones.
            const hasAnySelectedCategory = documentHolder.categories.some((category) => searchParams.categories.includes(category.name));
            if (!hasAnySelectedCategory) {
                return false;
            }

            // Then, IF any subCategories has been selected, make sure it has at least one of those.
            if (searchParams.subcategories) {
                const hasAnySelectedChildCategory = documentHolder.categories.some((category) => searchParams.subcategories.includes(category.name));
                if (!hasAnySelectedChildCategory) {
                    return false;
                }
            }

            return true;
        });
        return documents;
    };

    const handleSearchInput = (e: any) => {
        setSearchQuery(e.target.value.toLowerCase());
    };

    return (
        <ErrorCatcher componentName='Files'>
            <Show when={!documentHoldersData.loading} fallback={<LoadingPlaceHolder />}>
                <Section
                    templateShorthand={[12]}
                    widthType={'bgFull'}
                    heightType={'fill'}
                    backgroundType={'color'}
                    backgroundValue={'white'}
                    removeSpacingBlock={true}
                    customCss={'padding-top: 2rem; padding-bottom: 2rem;'}
                >
                    <Grid {...props.showFilterMenu ? {...gridSettings.container } : { templateShorthand: [12] }}>
                        <Show when={!isTabletOrSmaller() && props.showFilterMenu}>
                            <StyledSidebarContainer>
                                <StyledSidebarContainerLine>
                                    <StyledSidebar>
                                        <StyledSidebarInner>
                                            <StyledFilterHeadingContainer>
                                                <Heading tag='h2' variant='medium' noBlockSpacing={true}>{localize('filters', 'Filters')}</Heading>
                                            </StyledFilterHeadingContainer>

                                            <FilterList listHeading={localize('type', 'Type')}>
                                                <For each={types()}>{(type: Type) => (
                                                    <li>
                                                        <Radio
                                                            value={type.uuid}
                                                            whenClicked={(radioEvent) => {
                                                                if (radioEvent && radioEvent.target) {
                                                                    handleTypeSelect(type);
                                                                }
                                                            }}
                                                            name='type'
                                                            readableName={getFileSlugTranslations(localize, type.name)}
                                                            isChecked={getSearchParamAsArray(searchParams.type).includes(type.name)}
                                                        />
                                                    </li>
                                                )}</For>
                                            </FilterList>

                                            <Show when={topCategoriesToShow().length} >
                                                <FilterList listHeading={localize('category', 'Category')}>
                                                    <For each={topCategoriesToShow()}>{(category) => (
                                                        <li>
                                                            <Checkbox
                                                                value={category?.uuid}
                                                                whenClicked={(checkboxEvent) => {
                                                                    if (checkboxEvent && checkboxEvent.target) {
                                                                        const isChecked = checkboxEvent.target.checked;
                                                                        if (isChecked) {
                                                                            addTopCategory(category);
                                                                        } else {
                                                                            removeMainCategory(category);
                                                                        }
                                                                    }
                                                                }}
                                                                name={getFileSlugTranslations(localize, category?.name)}
                                                                isChecked={getSearchParamAsArray(searchParams.categories).includes(category.name)}
                                                            />
                                                        </li>
                                                    )}</For>
                                                </FilterList>
                                            </Show>

                                            <Show when={subCategoriesToShow().length}>
                                                <FilterList listHeading={localize('sub-category', 'Sub Category')}>
                                                    <For each={subCategoriesToShow()}>{(category) => (
                                                        <li>
                                                            <Checkbox
                                                                value={category.uuid}
                                                                whenClicked={(checkboxEvent) => {
                                                                    if (checkboxEvent && checkboxEvent.target) {
                                                                        const isChecked = checkboxEvent.target.checked;
                                                                        if (isChecked) {
                                                                            addSubCategory(category);
                                                                        } else {
                                                                            removeSubCategory(category);
                                                                        }
                                                                    }
                                                                }}
                                                                name={getFileSlugTranslations(localize, category.name)}
                                                                isChecked={getSearchParamAsArray(searchParams.subcategories).includes(category.name)}
                                                            />
                                                        </li>
                                                    )}</For>
                                                </FilterList>
                                            </Show>

                                            <Show when={selectedTypeId()}>
                                                <StyledClearAllContainer>
                                                    <Button
                                                        label={localize('clear-all', 'Clear all')}
                                                        onClick={(e) => clearFilters(e)}
                                                        variant='tertiary'
                                                        noCaps={true}
                                                    />
                                                </StyledClearAllContainer>
                                            </Show>

                                        </StyledSidebarInner>
                                    </StyledSidebar>
                                </StyledSidebarContainerLine>
                            </StyledSidebarContainer>
                        </Show>

                        <StyledProductsContainer>
                            <StyledHeadingWrapper>
                                <Heading tag='h2' variant='xxlarge'>
                                    { props.headline }
                                </Heading>
                            </StyledHeadingWrapper>

                            <StyledVerticalSpace size={3} />

                            <StyledFlexRow justifyContent={isMobile() ? 'center' : 'start'}>
                                <StyledSearchInput
                                    oninput={(e: any) => handleSearchInput(e)}
                                    placeholder={localize('search', 'Search...')}
                                />
                            </StyledFlexRow>

                            <StyledVerticalSpace size={2} />

                            <Show when={isTabletOrSmaller() && props.showFilterMenu}>
                                <MobileFilesFilter
                                    clickHandlers={{
                                        handleTypeClick: handleTypeSelect,
                                        handleTopCategoryClick: (category: Category, action: 'add' | 'remove') => {
                                            if (action === 'add') {
                                                addTopCategory(category);
                                            } else {
                                                removeMainCategory(category);
                                            }
                                        },
                                        handleSubCategoryClick: (category: Category, action: 'add' | 'remove') => {
                                            if (action === 'add') {
                                                addSubCategory(category);
                                            } else {
                                                removeSubCategory(category);
                                            }
                                        },
                                    }}

                                    selectedTypeId={selectedTypeId()}

                                    types={types()}
                                    topCategories={topCategoriesToShow()}
                                    subCategories={subCategoriesToShow()}

                                    clearFilters={clearFilters}
                                />

                                <Show when={selectedTypeId()}>
                                    <StyledClearAllContainer>
                                        <Button
                                            label={localize('clear-all', 'Clear all')}
                                            onClick={(e) => clearFilters(e)}
                                            variant='tertiary'
                                            noCaps={true}
                                        />
                                    </StyledClearAllContainer>
                                </Show>
                            </Show>


                            <StyledDocumentsList>
                                <For each={filteredDocuments()}>
                                    {(documentHolder: DocumentHolder) => (
                                        <StyledDocumentRow>
                                            <StyledDocument href={`/downloads/${documentHolder.id}/${documentHolder.slug}`} target="_blank">
                                                {documentHolder.name}
                                            </StyledDocument>
                                        </StyledDocumentRow>
                                    )}
                                </For>
                            </StyledDocumentsList>
                        </StyledProductsContainer>
                    </Grid>
                </Section>
            </Show>
        </ErrorCatcher>
    );
};

export type FilesProps = {
    types: string[];
    headline: string;
    showFilterMenu: string;
}
